'use client';
import { useState, useRef } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import Link from 'next/link';
import { ArrowLeft, Copy, Check, FileUp, Calculator } from 'lucide-react';

export default function CorrelationCalculatorPage() {
  const [csvInput, setCsvInput] = useState('');
  const [result, setResult] = useState('');
  const [col1, setCol1] = useState('0');
  const [col2, setCol2] = useState('1');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const calculateCorrelation = () => {
    try {
      setError('');
      if (!csvInput.trim()) {
        setError('Please enter CSV data');
        return;
      }

      const lines = csvInput.trim().split('\n');
      if (lines.length < 3) {
        setError('CSV must have at least a header row and two data rows');
        return;
      }

      const idx1 = parseInt(col1);
      const idx2 = parseInt(col2);
      
      const dataRows = lines.slice(1).map(line => line.split(',').map(v => v.trim()));
      const x = dataRows.map(row => parseFloat(row[idx1])).filter(v => !isNaN(v));
      const y = dataRows.map(row => parseFloat(row[idx2])).filter(v => !isNaN(v));

      if (x.length !== y.length || x.length === 0) {
        setError('Columns must contain numeric data and have the same number of valid entries');
        return;
      }

      const n = x.length;
      const sumX = x.reduce((a, b) => a + b, 0);
      const sumY = y.reduce((a, b) => a + b, 0);
      const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
      const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0);
      const sumY2 = y.reduce((sum, yi) => sum + yi * yi, 0);

      const numerator = (n * sumXY) - (sumX * sumY);
      const denominator = Math.sqrt(((n * sumX2) - (sumX * sumX)) * ((n * sumY2) - (sumY * sumY)));

      if (denominator === 0) {
        setResult('Correlation: 0 (No variation in data)');
      } else {
        const correlation = numerator / denominator;
        setResult(`Pearson Correlation Coefficient: ${correlation.toFixed(4)}\n\nInterpretation:\n${getInterpretation(correlation)}`);
      }
    } catch (err) {
      setError('Error calculating correlation. Please check your input format.');
    }
  };

  const getInterpretation = (r: number) => {
    const absR = Math.abs(r);
    if (absR >= 0.9) return 'Very strong relationship';
    if (absR >= 0.7) return 'Strong relationship';
    if (absR >= 0.5) return 'Moderate relationship';
    if (absR >= 0.3) return 'Weak relationship';
    return 'Very weak or no relationship';
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFileUpload = (file: File) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      setError('Please upload a CSV or TXT file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvInput(text);
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Correlation Calculator
              </h1>
            <p className="text-lg text-muted-foreground">
              Calculate the Pearson correlation coefficient between two columns
            </p>
          </div>

          {/* File Upload Area */}
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">Upload CSV File</label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
              }`}
            >
              <FileUp className={`w-12 h-12 mx-auto mb-4 ${
                isDragging ? 'text-blue-500' : 'text-gray-400'
              }`} />
              <p className="text-lg font-medium mb-2">
                {isDragging ? 'Drop your file here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-sm text-muted-foreground">
                Supports .csv and .txt files
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Column 1 Index (0-based)</label>
              <Input 
                type="number" 
                value={col1} 
                onChange={(e) => setCol1(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Column 2 Index (0-based)</label>
              <Input 
                type="number" 
                value={col2} 
                onChange={(e) => setCol2(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">CSV Input</label>
                <Textarea
                  placeholder="X,Y&#10;1,2&#10;2,4&#10;3,6"
                  value={csvInput}
                  onChange={(e) => setCsvInput(e.target.value)}
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>
              <Button 
                onClick={calculateCorrelation} 
                className="w-full"
                size="lg"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculate Correlation
              </Button>
              {error && (
                <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  {error}
                </div>
              )}
            </div>

            {/* Output Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Result</label>
                <Textarea
                  value={result}
                  readOnly
                  placeholder="Your correlation result will appear here..."
                  className="min-h-[200px] font-mono text-sm bg-muted"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={copyToClipboard} 
                  className="flex-1"
                  variant="outline"
                  disabled={!result}
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-4">Correlation FAQs</h2>
        <div className="space-y-6">
          <div><p className="font-semibold">1. What is Pearson Correlation?</p><p className="text-muted-foreground">It's a measure of the linear correlation between two variables, ranging from -1 to 1.</p></div>
          <div><p className="font-semibold">2. What does a correlation of 1 mean?</p><p className="text-muted-foreground">A perfect positive linear relationship. As one variable increases, the other increases proportionally.</p></div>
          <div><p className="font-semibold">3. What if my data is not numeric?</p><p className="text-muted-foreground">The tool will ignore non-numeric values. Ensure your selected columns contain numbers.</p></div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
